import { useCallback, useEffect, useState } from "react";
import { Modal, DatePicker, Spin, Empty } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { motion, AnimatePresence } from "framer-motion";
import { CREATE_jwel } from "../Config/Config";
import { MODE_META, fmtFull } from "./paymentModeMeta";

const { RangePicker } = DatePicker;

const cardVariant = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: Math.min(i * 0.03, 0.4),
      duration: 0.25,
      ease: "easeOut",
    },
  }),
};

/* which report each mode's data actually comes from — shown under the
   dialog title so it's clear where the numbers are sourced */
const SOURCE_LABEL = {
  CASH: "Cash Book",
  CARD: "Bank Transactions",
  UPI: "Bank Transactions",
  CHEQUE: "Bank Transactions",
  ONLINE: "Bank Transactions",
};

/* name/particulars shown next to the date on line 1 */
const getName = (mode, r) => (mode === "CASH" ? r.particulars : r.party || "—");

/* line 1, minus the voucher no — that gets its own badge in the UI */
const buildLine1Rest = (mode, r) =>
  `${dayjs(r.date).format("DD MMM YYYY")} · ${getName(mode, r)}`;

/* full line 1 text, used only for the row's title/tooltip */
const buildLine1 = (mode, r) => `#${r.vno} · ${buildLine1Rest(mode, r)}`;

/* line 2: everything else, joined into one compact meta string */
const buildLine2 = (mode, r) => {
  if (mode === "CASH") {
    const parts = [r.type];
    if (r.jewelType && r.jewelType !== "-") parts.push(r.jewelType);
    return parts.filter(Boolean).join(" @ ");
  }
  const parts = [r.transType];
  if (mode === "CHEQUE" && r.refNo && r.refNo !== "-") {
    parts.push(`Cheque No: ${r.refNo}`);
  }
  if (r.ptype && r.ptype !== "-") parts.push(r.ptype);
  return parts.filter(Boolean).join(" · ");
};

/**
 * Reusable single-mode transactions dialog. Each Payment Overview row
 * gets its own instance bound to exactly one `mode` — no in-dialog mode
 * switching. CASH reads CASH_BOOK (same shape as CashBookReports.jsx);
 * every other mode reads BANK_DEP_DRAW filtered client-side by `mode`
 * (same normalization aggregateBankByMode already used on the dashboard).
 */
const PaymentModeDialog = ({ open, mode, onClose }) => {
  const tenantName = localStorage.getItem("tenantName");
  const [dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setDateRange([dayjs(), dayjs()]);
  }, [open]);

  const fetchCash = useCallback(
    (from, to) => {
      const where = `CAST(BILLDATE AS DATE) >='${from}' AND CAST(BILLDATE AS DATE) <='${to}'`;
      return axios
        .get(
          `${CREATE_jwel}/api/Master/GetDataFromGivenTableNameWithWhereandOrder?tableName=CASH_BOOK&where=${encodeURIComponent(
            where,
          )}&order=sno`,
          { headers: { tenantName } },
        )
        .then((res) =>
          (res.data ?? []).map((item, idx) => ({
            key: idx + 1,
            vno: item.BILLNO,
            date: item.BILLDATE,
            particulars: item.PARTICULARS,
            type: item.TYPE,
            jewelType: item.JEWELTYPE,
            inward: Number(item.DEBIT || 0),
            outward: Number(item.CREDIT || 0),
          })),
        );
    },
    [tenantName],
  );

  const fetchBankMode = useCallback(
    (from, to, modeKey) => {
      const where = `CAST(DEPDATE AS DATE) >='${from}' AND CAST(DEPDATE AS DATE) <='${to}'`;
      return axios
        .get(
          `${CREATE_jwel}/api/Master/GetDataFromGivenTableNameWithWhereandOrder?tableName=BANK_DEP_DRAW&where=${encodeURIComponent(
            where,
          )}&order=${encodeURIComponent("DDCHR")}`,
          { headers: { tenantName } },
        )
        .then((res) =>
          (res.data ?? [])
            .filter(
              (item) =>
                String(item.mode || item.MODE || item.Mode || "")
                  .trim()
                  .toUpperCase() === modeKey,
            )
            .map((item, idx) => ({
              key: idx + 1,
              vno: item.RecNo || item.BNO,
              date: item.DEPDATE,
              transType: item.TRANSTYPE,
              party: item.CustName,
              refNo: item.dd_chequeno,
              ptype: item.PTYPE,
              inward: Number(item.Credit || 0),
              outward: Number(item.Debit || 0),
            })),
        );
    },
    [tenantName],
  );

  useEffect(() => {
    if (!open || !mode || !dateRange?.[0] || !dateRange?.[1]) return;
    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");
    setLoading(true);

    const request =
      mode === "CASH" ? fetchCash(from, to) : fetchBankMode(from, to, mode);

    request
      .then((data) => setRows(data))
      .catch((err) => {
        console.error("PaymentModeDialog fetch error:", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [open, mode, dateRange, fetchCash, fetchBankMode]);

  if (!mode) return null;

  const totals = rows.reduce(
    (acc, r) => {
      acc.inward += r.inward;
      acc.outward += r.outward;
      return acc;
    },
    { inward: 0, outward: 0 },
  );

  const meta = MODE_META[mode];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={850}
      centered
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      <div
        style={{
          background: `linear-gradient(135deg, ${meta.iconBg} 0%, ${meta.iconBg}cc 100%)`,
          padding: "18px 22px",
          borderRadius: "8px 8px 0 0",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              color: meta.iconColor,
              flexShrink: 0,
            }}
          >
            {meta?.icon}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>
              {meta?.label} Transactions
            </div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>
              {rows.length} record{rows.length === 1 ? "" : "s"} · From{" "}
              {SOURCE_LABEL[mode]}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          padding: "14px 18px",
          borderBottom: "1px solid #eee",
        }}
      >
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            if (dates?.[0] && dates?.[1]) setDateRange(dates);
          }}
          format="DD/MM/YYYY"
          size="large"
          allowClear={false}
          disabledDate={(c) => c && c.isAfter(dayjs(), "day")}
          suffixIcon={<CalendarOutlined />}
          style={{ fontSize: 14, minWidth: 260 }}
        />
        <div style={{ display: "flex", gap: 10 }}>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 800,
              color: "#fff",
              background: "#43aa8b",
              padding: "5px 18px",
              borderRadius: 10,
            }}
          >
            IN {fmtFull(totals.inward)}
          </span>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 800,
              color: "#fff",
              background: "#e05252",
              padding: "5px 18px",
              borderRadius: 10,
            }}
          >
            OUT {fmtFull(totals.outward)}
          </span>
        </div>
      </div>

      <div
        style={{
          maxHeight: "calc(100vh - 250px)",
          overflowY: "auto",
          padding: "8px 14px 12px",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <Empty
            description="No transactions found"
            style={{ padding: "30px 0" }}
          />
        ) : (
          <AnimatePresence>
            {rows.map((r, i) => (
              <motion.div
                key={r.key}
                custom={i}
                variants={cardVariant}
                initial="hidden"
                animate="visible"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 12px",
                  marginBottom: 6,
                  borderRadius: 8,
                  background: "#fafafa",
                  borderLeft: `3px solid ${meta.iconBg}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                    title={buildLine1(mode, r)}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#fff",
                        background: meta.iconBg,
                        padding: "2px 9px",
                        borderRadius: 6,
                        flexShrink: 0,
                        letterSpacing: 0.2,
                      }}
                    >
                      #{r.vno}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#2d1b69",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {buildLine1Rest(mode, r)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#777",
                      marginTop: 3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={buildLine2(mode, r)}
                  >
                    {buildLine2(mode, r)}
                  </div>
                </div>

                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  {r.inward > 0 && (
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#43aa8b",
                      }}
                    >
                      +{fmtFull(r.inward)}
                    </div>
                  )}
                  {r.outward > 0 && (
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#e05252",
                      }}
                    >
                      -{fmtFull(r.outward)}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </Modal>
  );
};

export default PaymentModeDialog;
